import type {
  IOrderConfirmationConfig,
  SessionDataArgs,
} from '../graphQL/queries/getSession/types'
import type {
  IOrderDetail,
  OrderExternalRefStatus,
} from '../graphQL/queries/order/types'
import {
  ORDER_POLL_INTERVAL_MS,
  ORDER_POLL_MIN_INTERVAL_MS,
  ORDER_POLL_TIMEOUT_MS,
} from '../redux/slices/orders/types'

/**
 * Every member of the backend's `OrderStatus` enum.
 *
 * Hand-mirrored rather than imported, for the reason
 * `ISessionOrderFilter.status` gives: cinnamon cannot reach the storefront's
 * `@engyalo/storefront-commons` types. Kept here so a configured status can be
 * checked against something, because the alternative is that a typo is only
 * ever observable as an order that polls to its deadline and then reports a
 * timeout it never had.
 *
 * Order follows the enum declaration, which is alphabetical apart from the two
 * trailing members.
 * @internal
 */
export const ORDER_STATUSES = [
  'CANCELLED',
  'CONFIRMED',
  'CREATED',
  'DELIVERED',
  'EDITED',
  'ERROR',
  'IN_PROGRESS',
  'PAID',
  'PROCESSED',
  'SENT',
  'SHIPPED',
  'SUGGESTED',
  'WAITING_CALLBACK',
] as const

/**
 * The confirmation parameters as the poll actually uses them: every field
 * present, every number usable.
 * @internal
 */
export interface IResolvedOrderConfirmation {
  /**
   * True only when the storefront declared `required: true`.
   *
   * Two effects, and it is worth keeping them apart. It turns the Order Detail
   * screen's `pending` and `timeout` states on — the *visible* confirming
   * affordance, meaningless where nothing confirms asynchronously. And it
   * decides what a bare `CREATED` order means to `deriveOrderOutcome`:
   * terminal (`created`) where the storefront declared no asynchronous
   * confirmation, still in flight (`pending`) where it did.
   *
   * What it does **not** do is suppress the poll for an order that reports
   * itself still in progress. `externalRefStatus: PROCESSING` and
   * `WAITING_CALLBACK` are re-read either way, because the alternative is a
   * screen showing a status the server has already moved on from.
   */
  isAsync: boolean
  /** Milliseconds between poll ticks. */
  pollingTime: number
  /**
   * Maximum number of poll ticks.
   *
   * `Infinity` when unconfigured, which reproduces COM-741 exactly: it bounded
   * the poll by wall clock alone and had no attempt concept. Whichever bound
   * is reached first ends the poll.
   */
  maxAttempts: number
  /** Milliseconds of wall clock before the client gives up. */
  timeout: number
  /**
   * `OrderStatus` values that mean the order settled successfully.
   *
   * Consumed by `deriveOrderOutcome`, **not** by `isOrderPending`. The two
   * answer different questions and one cannot be derived from the other: the
   * predicate asks whether the backend is still working on the order, these
   * lists say which statuses are final. Feeding them into the predicate would
   * make a bare `CREATED` order — one nothing may ever confirm — read as
   * pending and poll to the deadline, at the cost of a `completeBundlesExtra`
   * lookup per tick.
   *
   * Defaults to `DEFAULT_SUCCESS_STATUSES`.
   */
  successStatuses: string[]
  /**
   * `OrderStatus` values that mean the order settled as a failure.
   *
   * As `successStatuses`, and checked before it — see `deriveOrderOutcome`.
   * Defaults to `DEFAULT_FAILURE_STATUSES`.
   */
  failureStatuses: string[]
}

/**
 * The success statuses an unconfigured storefront gets.
 *
 * `CONFIRMED` alone: it is the only status the confirmation path itself
 * produces — `confirmOrder` sets it natively, and `validateExternalRef` sets
 * it when a callback lands. Every other member of the enum is a later stage of
 * fulfilment (`SENT`, `SHIPPED`, `DELIVERED`, `PAID` …) which an order reaches
 * long after the shopper has left this screen, so including one by default
 * would only widen what counts as settled without any order ever arriving in
 * it here.
 * @internal
 */
export const DEFAULT_SUCCESS_STATUSES = ['CONFIRMED']

/**
 * The failure statuses an unconfigured storefront gets.
 *
 * `ERROR` is what `markOrderAsError` and the order-error paths set; `CANCELLED`
 * is what `cancelOrder` sets. Both are terminal and neither is recoverable by
 * waiting, which is the whole test for membership here.
 *
 * `SUGGESTED` is deliberately absent even though it means the client system
 * changed the order: it is a *confirmable* status — `confirmOrder`'s filter
 * accepts it beside `CREATED` — so calling it a failure would abandon an order
 * one round trip from being confirmed.
 * @internal
 */
export const DEFAULT_FAILURE_STATUSES = ['ERROR', 'CANCELLED']

/**
 * The `ExternalRefStatus` values that mean the client system refused the order.
 *
 * Not configurable, and deliberately so. `successStatuses`/`failureStatuses`
 * exist because a storefront's *fulfilment* vocabulary is its own business —
 * whether `PROCESSED` counts as settled is a tenant question. These three are
 * not: they are the whole non-success half of the backend's `ExternalRefStatus`
 * enum (`PROCESSING`, `CONFIRMED`, `REJECTED`, `ERROR`, `TIMEOUT`), and there
 * is no reading of "the client system reported REJECTED" under which the
 * shopper should be told the order went through.
 *
 * `PROCESSING` is absent because it is the pending value, handled by
 * `isOrderPending`; `CONFIRMED` is absent because it is the success value and
 * arrives alongside `status: CONFIRMED` anyway.
 * @internal
 */
export const EXTERNAL_REF_FAILURE_STATUSES: OrderExternalRefStatus[] = [
  'REJECTED',
  'ERROR',
  'TIMEOUT',
]

/**
 * Whether the storefront splits orders server-side, and so forbids this flow.
 *
 * A mirror of the storefront's `isOrderSplit`: `configuration.splitters.order`
 * active **and** a non-zero `maxProduct`. A splitter authored with
 * `maxProduct: 0` splits nothing and the server reads it as off, so reading
 * `active` alone would refuse a storefront that is in fact fine.
 *
 * This exists because `createOrder` and `confirmOrder` do not degrade for a
 * splitting storefront — they `throw ERROR.BAD_REQUEST` immediately, naming
 * `createOrders`/`confirmOrders` instead — so an in-webview attempt fails on
 * every try, at the CTA, reported to the shopper as a generic creation
 * failure with no checkout rules named. Cinnamon does not implement the plural
 * mutations, so the honest answer is to decline the opt-in and take the legacy
 * path, which works for these storefronts today.
 *
 * Named for what it asks rather than what it gates, because the pair of
 * mutations is not the only thing that will ever care.
 * @internal
 */
export const isOrderSplitStorefront = (
  session?: SessionDataArgs | null,
): boolean => {
  const { active, maxProduct } = session?.configuration?.splitters?.order ?? {}
  return Boolean(active) && (maxProduct ?? 0) !== 0
}

/**
 * A non-empty list of strings, or the fallback.
 *
 * An empty array is treated as unconfigured rather than as "nothing is
 * terminal". The latter reading is never what an author means and its failure
 * is silent: every order would poll to its deadline and report an outcome
 * nobody could act on.
 *
 * **The non-array guard is live, but not for the reason it looks like.** A
 * genuine non-array cannot arrive: the SDL types these `[String]`, so output
 * serialization raises `Expected Iterable`, nullifies the field, and Apollo's
 * default `errorPolicy` then discards the whole response — see
 * `positiveNumber` below for that mechanism. What does arrive is `null`, and
 * routinely: GraphQL renders an absent field as `null` rather than omitting
 * it, so a block authoring only `required` sends `successStatuses: null` and
 * `!Array.isArray(null)` is this branch. It is the same partial-authoring
 * case that `OrderConfirmationSchema`'s `default: undefined` exists for
 * storefront-side. The per-member `typeof status === 'string'` filter is the
 * defensive half — the SDL and joi both constrain members to strings.
 */
const statusList = (value: unknown, fallback: string[]): string[] => {
  if (!Array.isArray(value)) return fallback
  const statuses = value.filter(
    (status): status is string =>
      typeof status === 'string' && status.length > 0,
  )
  return statuses.length ? statuses : fallback
}

/**
 * A positive, finite number, or the fallback.
 *
 * ⚠️ **These branches differ in whether they can fire through the wire, and
 * the ones that read as the point of the function are the defensive ones.**
 * Once Half A is serving, the SDL types these fields `Int` and joi constrains
 * them to non-negative integers, so scalar serialization sits in *front* of
 * this function, not behind it:
 *
 * - **The non-scalar branch is live and routine.** A partially authored block
 *   — `required: true` with no `pollingTime` — arrives as `pollingTime: null`,
 *   because GraphQL renders an absent field as `null` rather than omitting
 *   it. `typeof null` is `'object'`, so this is the branch every opted-in
 *   storefront that omits a key takes. Not defensive.
 * - **Rejecting `0` and negatives is live.** joi permits `0` deliberately
 *   (`nonNegativeIntegerConstraint`), so that an authoring slip stays inert
 *   in the consumer instead of becoming a write rejection in another system.
 *   That leaves this the only thing stopping a zero interval, which is a
 *   tight loop against the API with a `completeBundlesExtra` lookup per call.
 * - **Accepting numeric strings is defensive.** `"5000"` cannot arrive as a
 *   string — `GraphQLInt` coerces it to `5000` before cinnamon sees it — so
 *   read this branch as defence for a caller that is not this wire (a direct
 *   Mongo write, which is real here: COM-765) and for the tests, which pass
 *   raw strings through `as never`. Kept, but it is not what protects
 *   production.
 * - **`"soon"` never reaches this function**, and the mechanism is worth
 *   stating exactly, because "the server rejects it" is wrong. `Int`
 *   serialization fails on that one field; `pollingTime` is nullable, so
 *   graphql-js nullifies *just it* and still returns the rest of `data` with
 *   a partial `errors` entry. What throws the response away is the client —
 *   cinnamon's Apollo client sets no `errorPolicy`, so the default `'none'`
 *   discards `data` wholesale. The cost of a `"soon"` in Mongo is a failed
 *   boot, not a silent fallback.
 *
 * Verified against graphql-js 16.13.2 and the Half A SDL rather than assumed.
 */
const positiveNumber = (value: unknown, fallback: number): number => {
  if (typeof value !== 'number' && typeof value !== 'string') return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

/**
 * The poll interval: read as a positive number, then raised to
 * `ORDER_POLL_MIN_INTERVAL_MS` if it falls below it.
 *
 * The two steps are ordered, and the order is the whole design. A value that
 * is not positive at all — `0`, a negative, `null`, `"soon"` — is *absent* as
 * far as this file is concerned, so it takes `ORDER_POLL_INTERVAL_MS` and is
 * never clamped; clamping it would silently convert a broken value into a
 * working one five times faster than the default.
 *
 * Those four are not four live populations, and the list should not be read
 * as one: `0` arrives because joi permits it and `null` because GraphQL
 * renders an omitted key that way, a negative takes a direct Mongo write to
 * get past joi's `min(0)`, and `"soon"` never arrives at all — see
 * `positiveNumber` above. A value that *is* positive
 * but below the floor is a real instruction, honoured as closely as allowed.
 * So `0` resolves to 5000 and `5` resolves to 1000, and those two outcomes
 * differ on purpose.
 *
 * See `ORDER_POLL_MIN_INTERVAL_MS` for why the floor exists at all and why
 * `maxAttempts` and `timeout` deliberately have none.
 */
const pollingInterval = (value: unknown): number =>
  Math.max(
    positiveNumber(value, ORDER_POLL_INTERVAL_MS),
    ORDER_POLL_MIN_INTERVAL_MS,
  )

/**
 * Reads `configuration.order.confirmation` into the shape the poll uses.
 *
 * An absent block is the common case — it means the storefront has not opted
 * in — and it resolves to COM-741's own constants, so behaviour for every
 * storefront that predates this config is byte-identical.
 */
export const resolveOrderConfirmation = (
  config?: IOrderConfirmationConfig,
): IResolvedOrderConfirmation => ({
  // Exactly `true`. Remote JSON has no boolean guarantee, and a quoted
  // `"false"` is truthy — the same trap `packages.sort.enabled` documents.
  isAsync: config?.required === true,
  pollingTime: pollingInterval(config?.pollingTime),
  maxAttempts: positiveNumber(config?.maxAttempts, Number.POSITIVE_INFINITY),
  timeout: positiveNumber(config?.timeout, ORDER_POLL_TIMEOUT_MS),
  successStatuses: statusList(
    config?.successStatuses,
    DEFAULT_SUCCESS_STATUSES,
  ),
  failureStatuses: statusList(
    config?.failureStatuses,
    DEFAULT_FAILURE_STATUSES,
  ),
})

/**
 * Configured statuses that are not `OrderStatus` members.
 *
 * A guard, as of COM-776, and no longer merely a lint. `successStatuses` and
 * `failureStatuses` now reach `deriveOrderOutcome`, which is what decides
 * whether an order has settled — so a typo'd member is a status the derivation
 * can never match, and an order that reaches it polls to its deadline and then
 * reports a timeout it never had. That is the failure this names, and it is
 * silent without this check because a misspelt status is indistinguishable
 * from one the order simply has not reached yet.
 *
 * Note what it does not cover: a status listed under **both** keys. That is
 * authoring nonsense rather than a typo, every member of it is a real enum
 * value, and `deriveOrderOutcome` resolves it deterministically by checking
 * failures first.
 *
 * The caller reports these to Datadog rather than throwing — the screen still
 * works, and taking it down over a config typo would be the larger outage.
 */
export const getUnknownOrderStatuses = (
  config?: IOrderConfirmationConfig,
): string[] => {
  const configured = [
    ...(Array.isArray(config?.successStatuses) ? config.successStatuses : []),
    ...(Array.isArray(config?.failureStatuses) ? config.failureStatuses : []),
  ]
  return configured.filter(
    (status) =>
      !ORDER_STATUSES.includes(status as (typeof ORDER_STATUSES)[number]),
  )
}

/**
 * Whether the block was authored but will not switch the screen on.
 *
 * `required` is compared against literal `true` on purpose — remote JSON has
 * no boolean guarantee and a quoted `"false"` is truthy — but the cost of that
 * strictness is silent. A storefront that authors `successStatuses`,
 * `pollingTime` and `timeout` while writing `required: "true"`, or while
 * omitting `required` altogether, gets `isAsync: false`: the shopper is shown
 * a still-`PROCESSING` order as though it were final, and nothing anywhere
 * says so. It is the likelier of the two authoring mistakes this file guards
 * against — a typo'd status at least has to be typed.
 *
 * Three things are deliberately not this:
 * - an **absent** block, which is every storefront that has not opted in;
 * - an **empty** one, where nothing was authored to contradict;
 * - a literal `required: false`, which is an explicit opt-out and a decision
 *   rather than a slip, even beside a fully authored block.
 *
 * Reported by the caller, never thrown, for the reason
 * `getUnknownOrderStatuses` gives.
 */
export const isRequiredMisauthored = (
  config?: IOrderConfirmationConfig,
): boolean => {
  if (!config || typeof config !== 'object') return false
  if (!Object.keys(config).length) return false
  return config.required !== true && config.required !== false
}

/**
 * Whether the client's backend is still working on this order.
 *
 * Two pending shapes, and neither field alone covers both.
 *
 * `externalRefStatus === 'PROCESSING'` is the external-ref path. Testing
 * `status === 'CREATED'` instead would be wrong: it also covers orders that
 * were never submitted anywhere, so the screen would tick at something that is
 * never going to move — and every tick costs a `completeBundlesExtra` lookup
 * server-side.
 *
 * `status === 'WAITING_CALLBACK'` is the callback path, and it is only
 * expressible on `status`. The CREATE_ORDER add-on sets it when it has
 * `hasCallback`, before any external ref exists; `validateExternalRef` sets
 * `externalRefStatus` only once the callback lands. So a callback-path order
 * carries no `externalRefStatus` while it waits, and testing that field alone
 * would never poll it at all.
 *
 * Shared by the poll, which decides whether to tick again, and by the panel
 * status selector, which decides whether to say "confirming". Two copies of
 * this predicate would be two answers to the same question.
 */
export const isOrderPending = (order?: IOrderDetail | null): boolean =>
  order?.externalRefStatus === 'PROCESSING' ||
  order?.status === 'WAITING_CALLBACK'

/**
 * Where an order stands, as the in-webview confirmation flow reads it.
 *
 * Three terminal members and one that is not. The names are the same words
 * `IOrdersState.creationStatus` uses, so the flow can dispatch what this
 * returns without a translation table in between — and so the confirmation
 * screen, which reads the slice, and the poll, which reads this, cannot drift
 * into two vocabularies for one fact.
 *
 * `creating`, `confirming` and `unknown` are absent because they are not
 * properties of an order: the first two are requests in flight and the third
 * is the client having given up. Those live on the slice.
 * @internal
 */
export type OrderOutcome = 'pending' | 'created' | 'confirmed' | 'failed'

/**
 * Whether an order has settled, and how.
 *
 * The single place terminality is decided. Written here rather than in the
 * flow hook because its other consumer is the confirmation screen, which
 * renders the outcome and must reach the same verdict the poll reached when it
 * stopped ticking — two copies would be two answers to one question, which is
 * the trap `isOrderPending` was extracted to avoid.
 *
 * The order of the five checks is the whole content of this function, and the
 * two fields have to be read together because the backend moves them
 * independently:
 *
 * 1. **A terminal `externalRefStatus` outranks everything, `status`
 *    included.** `validateExternalRef` — the callback handler — assigns
 *    `order.externalRefStatus = externalRefStatus || CONFIRMED` and then calls
 *    `updateOrderAndSendEvent` with `status: OrderStatus.CONFIRMED`
 *    **unconditionally**, with no branch on the value the client system
 *    reported. So a client that calls back with `REJECTED` (or `ERROR`, or
 *    `TIMEOUT` — joi accepts all three) produces
 *    `{ status: CONFIRMED, externalRefStatus: REJECTED }`. Reading `status`
 *    first would tell the shopper a rejected order succeeded, which by check
 *    2's own reasoning is the expensive direction.
 * 2. **Failures before successes.** A status in both lists is authoring
 *    nonsense that `getUnknownOrderStatuses` cannot catch, so it has to resolve
 *    one way; failure is the safer reading. Telling a shopper an order failed
 *    when it succeeded costs them a phone call, telling them it succeeded when
 *    it failed costs them a delivery they will wait for.
 * 3. **A reached terminal status beats `isOrderPending`.** This is the
 *    ordering COM-776 got wrong and shipped nowhere: the pending predicate
 *    used to run first and be unoverridable, which made a confirmed order on
 *    an add-on storefront unreportable. `createOrderAddon` writes
 *    `{ $set: { externalRefStatus: PROCESSING } }` unconditionally, before its
 *    `hasCallback` branch; `externalRefStatus` has no schema default and
 *    `GenericModel.update` merges only the keys the update object carries, so
 *    the value survives every later write; and the native `confirmOrder` sets
 *    `status: CONFIRMED` without touching `externalRefStatus` — a repo-wide
 *    search finds exactly three writers, the two create-order add-ons and the
 *    callback handler. A confirmed order on such a storefront is therefore
 *    `{ status: CONFIRMED, externalRefStatus: PROCESSING }` for good, and
 *    pending-first burnt the whole `maxAttempts`/`timeout` budget to report
 *    `unknown`. Letting the configured lists win costs nothing the guards do
 *    not already cover: `WAITING_CALLBACK` and a bare `CREATED` are in neither
 *    default list, so both still fall through to the predicate.
 * 4. **`isAsync` decides what a merely-`CREATED` order means.** Where the
 *    storefront declared no asynchronous confirmation, nothing is going to
 *    move this order on the shopper's timescale and `created` is the honest
 *    terminal answer — the order was placed and the store will confirm it.
 *    Where it did, the same status means confirmation has not landed yet, so
 *    the safety net keeps polling.
 * 5. Everything else is `pending`, bounded by `maxAttempts` and `timeout`.
 *
 * An absent order reads as `pending`: the flow has nothing to settle on yet,
 * and `created` — the only other candidate for "no evidence" — would announce
 * an order that may not exist.
 */
export const deriveOrderOutcome = (
  order: IOrderDetail | null | undefined,
  config?: IOrderConfirmationConfig,
): OrderOutcome => {
  if (!order) return 'pending'

  const { isAsync, successStatuses, failureStatuses } =
    resolveOrderConfirmation(config)
  const status = order.status ?? ''
  const { externalRefStatus } = order

  // Guarded on presence rather than defaulted to `''`: an empty string is not
  // an `ExternalRefStatus` and using one as a sentinel invites it being
  // compared against a real value somewhere later.
  if (
    externalRefStatus &&
    EXTERNAL_REF_FAILURE_STATUSES.includes(externalRefStatus)
  )
    return 'failed'
  if (failureStatuses.includes(status)) return 'failed'
  if (successStatuses.includes(status)) return 'confirmed'
  if (isOrderPending(order)) return 'pending'
  if (!isAsync) return 'created'
  return 'pending'
}

export default resolveOrderConfirmation
